import {
    GetProductsSearchOptionType,
    GetProductsSortOptionType,
    SearchType,
    SortDirectionType,
} from "@models/productFilter";
import type { SearchFilters } from "../useSearchFilters";

export const PRODUCTS_PAGE_SIZE = 20;

// One query builder for every filterable product read (#401 B4): the search
// results page (with a term) and the category page (without one). Everything
// useSearchFilters owns — category, price cap, brand, vendor, in-stock and
// sort — reaches GET /products the same way from both. `q` is optional:
// without it the search parameters are omitted entirely rather than sent
// empty.
export const buildSearchUrl = (
    q: string | undefined,
    pageNumber: number,
    filters: SearchFilters,
) => {
    const params = new URLSearchParams();
    if (q) {
        params.set("search", q);
        params.set("searchType", String(SearchType.Contains));
        params.append("searchBy", String(GetProductsSearchOptionType.Name));
    }
    params.set("pageNumber", String(pageNumber));
    params.set("pageSize", String(PRODUCTS_PAGE_SIZE));
    // /products accepts a repeated categoryIds key (bound to CategoryIds on
    // GetProductsQuery, which expands each id to its descendants). Single-id
    // and no-id are both valid; a NaN (an unparsable ?category= or route
    // param) is not an id and is never put on the wire — `categoryIds=NaN`
    // is a 400.
    filters.categoryIds
        .filter((id) => Number.isInteger(id))
        .forEach((id) => params.append("categoryIds", String(id)));
    // Facet filters (#226). Each one is omitted ENTIRELY when it is not
    // constraining — `maxPrice=` or `inStockOnly=false` would ask the server to
    // filter on nothing, and "selected nothing" must not narrow the results.
    // Note `!== null` rather than a truthy test: null is the one value that
    // means "no cap", and it must stay distinct from any number on the ladder.
    // The four facet keys bind to MaxPrice, Brands, VendorIds and InStockOnly on
    // GetProductsQuery. Brands and vendors OR within the facet and AND across
    // facets; a vendor + in-stock pair is satisfied by the SAME supply row.
    if (filters.maxPrice !== null) {
        params.set("maxPrice", String(filters.maxPrice));
    }
    filters.brands.forEach((brand) => params.append("brands", brand));
    filters.vendorIds.forEach((id) => params.append("vendorIds", String(id)));
    // The In-Stock toggle itself is only offered once the facet read has
    // answered — see shouldOfferInStockFilter.
    if (filters.inStockOnly) params.set("inStockOnly", "true");
    // Sort: the two price options map to GetProductsSortOptionType.Price (the
    // base sku's price) with a direction.
    // TODO(backend): popularity and on-sale have no GetProductsSortOptionType
    // member, so SortPanel hides them (audit BR-08); re-adding one means
    // SORT_VALUES in useSearchFilters, SortPanel, and a mapping here.
    // Amount likewise has no API field, and its chips are hidden for MVP (see
    // FilterPanel's TODO(post-MVP)) though the dimension still round-trips
    // through the URL.
    if (filters.sort === "price-asc" || filters.sort === "price-desc") {
        params.set("sortBy", String(GetProductsSortOptionType.Price));
        params.set(
            "sortDirection",
            String(
                filters.sort === "price-asc"
                    ? SortDirectionType.Asc
                    : SortDirectionType.Desc,
            ),
        );
    }
    return `/products?${params.toString()}`;
};
